import { useEffect, useState, type FormEvent } from 'react';
import { adminApi, errorText, type ProjectSettings } from './api';
import { ImageUpload } from './image-upload';

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null);

export function ProjectPage() {
  const [project, setProject] = useState<ProjectSettings | null>(null);
  const [form, setForm] = useState({ name: '', senderEmail: '', senderName: '', vacationFrom: '', vacationUntil: '', scheme: 'light', brand: '#1f4e5f', action: '', font: 'sans', logoUrl: '', brevoListId: '', newsletterOffered: false, retentionDays: '365' });
  const [listsBusy, setListsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fill = (p: ProjectSettings) => {
    setProject(p);
    setForm({
        name: p.name,
        senderEmail: p.senderEmail ?? '',
        senderName: p.senderName ?? '',
        vacationFrom: toLocalInput(p.vacationFrom),
        vacationUntil: toLocalInput(p.vacationUntil),
        scheme: p.theme.scheme ?? 'light',
        brand: p.theme.brand ?? '#1f4e5f',
        action: p.theme.action ?? '',
        font: p.theme.font ?? 'sans',
        logoUrl: p.theme.logoUrl ?? '',
        brevoListId: p.brevoListId === null ? '' : String(p.brevoListId),
        newsletterOffered: p.newsletterOffered,
        retentionDays: String(p.retentionDays),
      });
  };
  useEffect(() => {
    adminApi.getProject().then(fill);
  }, []);

  async function createLists() {
    setListsBusy(true);
    setError(null);
    try {
      fill(await adminApi.createBrevoList());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setListsBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const p = await adminApi.updateProject({
        name: form.name,
        senderEmail: form.senderEmail || null,
        senderName: form.senderName || null,
        vacationFrom: fromLocalInput(form.vacationFrom),
        vacationUntil: fromLocalInput(form.vacationUntil),
        theme: {
          scheme: form.scheme as 'light' | 'dark',
          brand: form.brand,
          ...(form.action ? { action: form.action } : {}),
          font: form.font as 'sans' | 'serif',
          ...(form.logoUrl ? { logoUrl: form.logoUrl } : {}),
        },
        brevoListId: form.brevoListId.trim() === '' ? null : Math.round(Number(form.brevoListId)),
        newsletterOffered: form.newsletterOffered,
        retentionDays: Math.round(Number(form.retentionDays)),
      });
      setProject(p);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (!project) return <p className="muted">Wird geladen …</p>;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [key]: e.target.value });
  const publicHost = project.customDomain ?? `${project.slug}.${window.location.hostname.replace(/^app\./, '')}`;

  return (
    <>
      <div className="admin-head">
        <h1>Projekt</h1>
      </div>
      <form onSubmit={submit} className="admin-grid">
        <div className="card">
          <h2 className="card__title">Allgemein</h2>
          <div className="field">
            <label htmlFor="p-name">Name des Projekts</label>
            <input id="p-name" type="text" value={form.name} onChange={set('name')} maxLength={200} required />
            <span className="faint small">Erscheint in der Kopfzeile des Raums und als Absendername.</span>
          </div>
          <div className="field">
            <label>Adresse der Teilnehmerseiten</label>
            <div>
              <a href={`https://${publicHost}`} target="_blank" rel="noopener">
                https://{publicHost}
              </a>
            </div>
            <span className="faint small">Eine eigene Domain richtet der Betreiber ein – sie zeigt dann auf denselben Server.</span>
          </div>
        </div>

        <div className="card">
          <h2 className="card__title">Absender der E-Mails</h2>
          <div className="field">
            <label htmlFor="p-sender-email">Absenderadresse</label>
            <input id="p-sender-email" type="email" value={form.senderEmail} onChange={set('senderEmail')} placeholder="webinar@deine-domain.de" />
            <span className="faint small">Muss bei Brevo als Absender bestätigt sein, die Domain mit DKIM – sonst landet alles im Spam. Leer = Absender der Plattform.</span>
          </div>
          <div className="field">
            <label htmlFor="p-sender-name">Absendername</label>
            <input id="p-sender-name" type="text" value={form.senderName} onChange={set('senderName')} placeholder={form.name} />
          </div>
        </div>

        <div className="card">
          <h2 className="card__title">Erscheinungsbild</h2>
          <p className="muted small">Ein Aufbau für alle Projekte – hier stellst du Farben, Grundschema und Schrift ein.</p>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="p-scheme">Grundschema</label>
              <select id="p-scheme" value={form.scheme} onChange={set('scheme')}>
                <option value="light">hell</option>
                <option value="dark">dunkel</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="p-font">Schrift</label>
              <select id="p-font" value={form.font} onChange={set('font')}>
                <option value="sans">serifenlos (modern)</option>
                <option value="serif">Serifen (wärmer)</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="p-brand">Markenfarbe</label>
              <div className="color-row">
                <input type="color" value={form.brand} onChange={set('brand')} aria-label="Markenfarbe wählen" />
                <input id="p-brand" type="text" value={form.brand} onChange={set('brand')} pattern="#[0-9a-fA-F]{6}" />
              </div>
            </div>
            <div className="field">
              <label htmlFor="p-action">Farbe der Knöpfe</label>
              <div className="color-row">
                <input type="color" value={form.action || form.brand} onChange={set('action')} aria-label="Knopffarbe wählen" />
                <input id="p-action" type="text" value={form.action} onChange={set('action')} placeholder="wie Markenfarbe" pattern="#[0-9a-fA-F]{6}" />
              </div>
            </div>
          </div>
          <div className="field">
            <label>Logo</label>
            <ImageUpload
              current={project.logoPath}
              alt="Logo"
              hint="PNG mit transparentem Hintergrund oder JPG, etwa 400 × 120 Bildpunkte, höchstens 5 MB. Ohne Logo steht der Projektname als Schriftzug."
              onUpload={async (file) => {
                await adminApi.uploadLogo(file);
                fill(await adminApi.getProject());
              }}
              onRemove={async () => {
                await adminApi.removeLogo();
                fill(await adminApi.getProject());
              }}
            />
          </div>
          {!project.logoPath && (
            <div className="field">
              <label htmlFor="p-logo">… oder Adresse eines Bildes im Netz (https)</label>
              <input id="p-logo" type="url" value={form.logoUrl} onChange={set('logoUrl')} placeholder="https://…/logo.png" />
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="card__title">Abwesenheit</h2>
          <p className="muted small">In diesem Zeitraum werden keine Termine angeboten – damit kein Webinar läuft, während niemand erreichbar ist.</p>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="p-vac-from">Von</label>
              <input id="p-vac-from" type="datetime-local" value={form.vacationFrom} onChange={set('vacationFrom')} />
            </div>
            <div className="field">
              <label htmlFor="p-vac-until">Bis</label>
              <input id="p-vac-until" type="datetime-local" value={form.vacationUntil} onChange={set('vacationUntil')} />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="card__title">Brevo-Kontakte</h2>
          <p className="muted small">
            Alle Anmeldungen landen als Kontakte in dieser einen Liste. Was jemand getan hat, steht als Attribut am Kontakt – darauf baust du in Brevo deine Segmente und Mail-Serien:
          </p>
          <ul className="muted small" style={{ margin: '0 0 12px', paddingLeft: 18 }}>
            <li><code>VORNAME</code>, <code>WEBINAR</code>, <code>TERMIN</code></li>
            <li><code>WEBINAR_STATUS</code>: angemeldet · teilgenommen · nicht erschienen · abgesagt</li>
            <li><code>ANGEBOT_GEKLICKT</code>: ja/nein</li>
            <li><code>NEWSLETTER</code> und <code>NEWSLETTER_AM</code>: per Double-Opt-in bestätigt – <strong>Werbe-Serien nur an NEWSLETTER = ja</strong></li>
          </ul>
          <div className="field">
            <label htmlFor="p-brevo-list">Liste (Nummer bei Brevo)</label>
            <input id="p-brevo-list" type="number" min={1} value={form.brevoListId} onChange={set('brevoListId')} placeholder="leer = kein Abgleich" />
          </div>
          {project.brevoAvailable ? (
            project.brevoListId === null && (
              <div className="row-actions" style={{ marginBottom: 12 }}>
                <button type="button" className="button button--secondary button--small" disabled={listsBusy} onClick={() => void createLists()}>
                  {listsBusy ? 'Lege an …' : 'Liste bei Brevo anlegen'}
                </button>
                <span className="faint small">Im Ordner „Evercast", samt Attributen.</span>
              </div>
            )
          ) : (
            <p className="faint small">Brevo ist auf diesem Server nicht angeschlossen – die Nummer kannst du trotzdem eintragen.</p>
          )}
          <label className="choice">
            <input type="checkbox" checked={form.newsletterOffered} onChange={(e) => setForm({ ...form, newsletterOffered: e.target.checked })} />
            <span>
              Newsletter-Häkchen auf der Anmeldeseite anbieten <span className="muted small">– wer es setzt, bekommt eine Bestätigungs-Mail; erst nach dem Klick steht NEWSLETTER auf ja.</span>
            </span>
          </label>
        </div>

        <div className="card">
          <h2 className="card__title">Datenschutz</h2>
          <p className="muted small">
            Löschkonzept: So viele Tage nach dem Termin werden Anmeldungen anonymisiert – Adresse, Name und Zugang verschwinden, Anschau-Meldungen und Fragen dazu werden gelöscht. Die Zahlen (Teilnahme, Sehdauer) bleiben für die Statistik. Kontakte bei Brevo sind davon nicht betroffen.
          </p>
          <div className="field">
            <label htmlFor="p-retention">Aufbewahrung in Tagen (30 bis 3650)</label>
            <input id="p-retention" type="number" min={30} max={3650} step={1} required value={form.retentionDays} onChange={set('retentionDays')} />
          </div>
        </div>

        <div className="save-bar" style={{ gridColumn: '1 / -1' }}>
          <button type="submit" className="button" disabled={busy}>
            {busy ? 'Speichert …' : 'Speichern'}
          </button>
          {saved && <span className="muted small">Gespeichert.</span>}
          {error && <span className="error-inline">{error}</span>}
        </div>
      </form>

      <PasswordCard />
    </>
  );
}

/** Eigenes Passwort ändern – beendet alle anderen Sitzungen dieses Zugangs. */
function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);
    if (next !== repeat) return setError('Die Wiederholung stimmt nicht mit dem neuen Passwort überein.');
    if (next.length < 10) return setError('Das neue Passwort braucht mindestens 10 Zeichen.');
    setBusy(true);
    try {
      await adminApi.changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" style={{ marginTop: 24, maxWidth: 520 }} onSubmit={submit}>
      <h2 className="card__title">Passwort ändern</h2>
      <p className="muted small">Mindestens 10 Zeichen. Andere Geräte, auf denen du angemeldet bist, werden dabei abgemeldet.</p>
      <div className="field">
        <label htmlFor="pw-current">Bisheriges Passwort</label>
        <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="pw-next">Neues Passwort</label>
          <input id="pw-next" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="pw-repeat">Noch einmal</label>
          <input id="pw-repeat" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
        </div>
      </div>
      <div className="save-bar">
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Ändert …' : 'Passwort ändern'}
        </button>
        {done && <span className="muted small">Geändert.</span>}
        {error && <span className="error-inline">{error}</span>}
      </div>
    </form>
  );
}
